import QuoteService from './QuoteService'

// Matches the cadence the task bar and the Comms tab each used to poll at.
const REFRESH_MS = 30000

const CONTENT_TYPES = ['sms', 'whatsApp', 'email', 'remarks', 'images', 'documents']

const emptyCounts = () =>
  CONTENT_TYPES.reduce((acc, type) => {
    acc[type] = null
    return acc
  }, {})

// A content type the server could not count comes back null. Treat it as nothing to
// show rather than as zero, so a broken count leaves its indicator alone instead of
// quietly reporting "all read".
const unreadOf = (counts, type) => (counts[type] ? counts[type].unread : 0)
const totalOf = (counts, type) => (counts[type] && counts[type].total ? counts[type].total : 0)

const state = {
  quoteId: null,
  counts: emptyCounts(),
  // When each type was last cleared locally, and when the in-flight request started.
  // A response that left before the user read something must not put the dot back on.
  zeroedAt: {},
  requestStartedAt: 0,
  interval: null
}

const getters = {
  counts: (state) => state.counts,

  smsUnreadCount: (state) => unreadOf(state.counts, 'sms'),
  whatsAppUnreadCount: (state) => unreadOf(state.counts, 'whatsApp'),
  emailUnreadCount: (state) => unreadOf(state.counts, 'email'),
  remarkUnreadCount: (state) => unreadOf(state.counts, 'remarks'),
  imageUnreadCount: (state) => unreadOf(state.counts, 'images'),
  documentUnreadCount: (state) => unreadOf(state.counts, 'documents'),

  smsTotalCount: (state) => totalOf(state.counts, 'sms'),
  whatsAppTotalCount: (state) => totalOf(state.counts, 'whatsApp'),
  emailTotalCount: (state) => totalOf(state.counts, 'email'),
  imageCount: (state) => totalOf(state.counts, 'images'),
  documentCount: (state) => totalOf(state.counts, 'documents'),

  commsUnreadCount: (state) =>
    unreadOf(state.counts, 'sms') + unreadOf(state.counts, 'whatsApp') + unreadOf(state.counts, 'email'),
  commsTotalCount: (state) =>
    totalOf(state.counts, 'sms') + totalOf(state.counts, 'whatsApp') + totalOf(state.counts, 'email')
}

const actions = {
  // Called once when a quote opens. Safe to call again for the same quote - the poll
  // is replaced rather than doubled, which is what a component remounting would do.
  async start({ state, commit, dispatch }, quoteId) {
    if (state.interval) {
      clearInterval(state.interval)
    }

    commit('setQuote', quoteId)

    // Armed before the first read, not after it. Arming afterwards made the poll's
    // existence depend on that one request succeeding - a single hiccup and the counts
    // stood still for as long as the quote stayed open, with nothing to correct them.
    const interval = setInterval(() => dispatch('refresh'), REFRESH_MS)
    commit('setInterval', interval)

    await dispatch('refresh')
  },

  stop({ state, commit }) {
    if (state.interval) {
      clearInterval(state.interval)
    }
    commit('reset')
  },

  async refresh({ state, commit }) {
    if (!state.quoteId) return

    const quoteId = state.quoteId
    const startedAt = Date.now()
    commit('setRequestStartedAt', startedAt)

    let counts
    try {
      counts = await QuoteService.getContentCounts(quoteId)
    } catch (e) {
      // Leave the last known counts where they are and let the next poll recover.
      // Writing nothing is deliberate: an empty payload reads as every type being
      // zero, which shows no badges and no indicators - a confident "nothing here"
      // rather than an out-of-date number.
      console.error('Content counts could not be read', e)
      return
    }

    // The quote changed under us while the request was in flight.
    if (state.quoteId !== quoteId) return

    commit('setCounts', { counts, startedAt })
  },

  // A tab was opened, so its content is read. Clearing here rather than waiting for
  // the next refresh is what makes the indicator go out immediately.
  markRead({ commit }, type) {
    commit('zeroUnread', type)
  }
}

const mutations = {
  setQuote(state, quoteId) {
    if (state.quoteId !== quoteId) {
      state.counts = emptyCounts()
      state.zeroedAt = {}
    }
    state.quoteId = quoteId
  },

  setInterval(state, interval) {
    state.interval = interval
  },

  setRequestStartedAt(state, startedAt) {
    state.requestStartedAt = startedAt
  },

  setCounts(state, { counts, startedAt }) {
    const next = {}

    CONTENT_TYPES.forEach((type) => {
      const incoming = counts ? counts[type] : null

      // This type was read after the request went out, so its unread number is stale.
      // Keep the total, which is still the newest thing we know, and hold the
      // indicator off until a request that started after the read comes back.
      if (incoming && state.zeroedAt[type] > startedAt) {
        next[type] = { total: incoming.total, unread: 0 }
      } else {
        next[type] = incoming
      }
    })

    state.counts = next
  },

  zeroUnread(state, type) {
    state.zeroedAt = { ...state.zeroedAt, [type]: Date.now() }

    const current = state.counts[type]
    if (current) {
      state.counts = { ...state.counts, [type]: { total: current.total, unread: 0 } }
    }
  },

  reset(state) {
    state.quoteId = null
    state.counts = emptyCounts()
    state.zeroedAt = {}
    state.requestStartedAt = 0
    state.interval = null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
